/* eslint-disable */
import {
  Box,
  BoxProps,
  FormControl,
  FormHelperTextProps,
  IconButtonProps,
  InputLabel,
  Select as MaterialSelect,
  SelectProps as MaterialSelectProps,
} from '@mui/material';
import { ChevronDownSmall, Lock } from '@portals/icons';
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FormHelperText } from '@portals/core/src/components/FormHelperText/FormHelperText';
import { IconButton } from '@portals/core/src/components/IconButton/IconButton';

export interface SelectFieldProps extends Omit<MaterialSelectProps, 'onChange'> {
  id: string;

  /**
   * Provide a readonly locked select
   */
  locked?: boolean;

  /**
   * Add a HelperText Component
   */
  helperText?: FormHelperTextProps['children'];

  /**
   * Provide a datalist for the input field
   */
  datalist?: React.ReactElement;

  /**
   * Provide a datalist id for the input field
   */
  datalistId?: string;

  /**
   * Focus Event: onBlur
   * @see [https://reactjs.org/docs/events.html#onblur]
   */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /**
   * Focus Event: onFocus
   * @see [https://reactjs.org/docs/events.html#onfocus]
   */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /**
   * Change Event: onChange
   * @see [https://reactjs.org/docs/events.html#onchange]
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

export const SelectField: React.FC<SelectFieldProps> = React.memo(
  ({
    id,
    label,
    multiple,
    locked,
    children,
    slotProps,
    disabled,
    helperText,
    datalist,
    datalistId,
    error,
    required,
    ...props
  }) => {
    const [open, setOpen] = useState(false);
    const [isOpenClick, setIsOpenClick] = useState(false);
    const selectRef = useRef<HTMLSelectElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const InputProps = useMemo(() => {
      if (datalistId) {
        return { list: datalistId, required, ...slotProps?.input };
      }

      return { required, ...slotProps?.input };
    }, [required, datalistId, slotProps?.input]);

    const clickListener = useCallback(
      (event: Event) => {
        if (!open || isOpenClick) {
          setIsOpenClick(false);
          return;
        }

        if (menuRef.current?.contains(event.target as Node)) {
          if (!multiple) {
            setOpen(false);
          }
          return;
        }
        event.stopPropagation();
        event.preventDefault();
        setOpen(false);
      },
      [isOpenClick, multiple, open]
    );

    const keyListener = useCallback((event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setOpen(false);
      }
    }, []);

    const doClose = useCallback(() => {
      setOpen(false);
    }, []);

    const doOpen = useCallback(
      (event: SyntheticEvent<Element, Event>) => {
        if (!locked && !open) {
          if (event.type === 'mousedown') {
            setIsOpenClick(true);
          }
          setOpen(true);
        }
      },
      [locked, open]
    );

    const doToggle = useCallback(() => {
      if (!locked) {
        setOpen((open) => !open);
      }
    }, [locked]);

    const scrollListener = useCallback(() => {
      setOpen(false);
    }, []);

    useEffect(() => {
      if (open) {
        document.addEventListener('click', clickListener, true);
        window.addEventListener('keydown', keyListener);
      }

      return () => {
        document.removeEventListener('click', clickListener, true);
        window.removeEventListener('keydown', keyListener);
      };
    }, [clickListener, keyListener, open, scrollListener]);

    const iconComponent = useCallback(
      (_props: BoxProps & IconButtonProps) =>
        locked ? (
          <Box {..._props}>
            <Lock fontSize="small" />
          </Box>
        ) : (
          <IconButton {..._props} onClick={doToggle} tabIndex={-1}>
            <ChevronDownSmall fontSize="inherit" />
          </IconButton>
        ),
      [locked, doToggle]
    );

    const MenuProps = useMemo(() => {
      return {
        disableAutoFocusItem: true,
        id: `${id}-menu`,
        sx: { pointerEvents: 'none' },
        PaperProps: {
          ref: menuRef,
          sx: { pointerEvents: 'all' },
        },
        ...props.MenuProps,
      };
    }, [id, props.MenuProps]);

    return (
      <FormControl required={required} fullWidth error={error} data-test={props?.name}>
        <InputLabel id={`${id}-label`} className={locked ? 'Mui-locked' : ''} sx={props?.sx}>
          {label}
        </InputLabel>
        <MaterialSelect
          id={id}
          labelId={`${id}-label`}
          label={label}
          multiple={multiple}
          open={open}
          onOpen={doOpen}
          onClose={doClose}
          ref={selectRef}
          IconComponent={iconComponent}
          disabled={disabled || locked}
          className={locked ? 'Mui-locked' : ''}
          required={required}
          {...props}
          MenuProps={MenuProps}
          slotProps={{
            ...slotProps,
            input: InputProps,
          }}
          onChange={props.onChange as MaterialSelectProps['onChange']}
          variant={props.variant as 'filled' | 'outlined' | 'standard'}
        >
          {children}
        </MaterialSelect>
        {datalist}
        {helperText ? <FormHelperText error={error}>{helperText}</FormHelperText> : null}
      </FormControl>
    );
  }
);

SelectField.displayName = 'SelectField';
